import { useQuery } from '@apollo/client';
import { NextRouter } from 'next/router';
import { useRef, useState } from 'react';
import {
  AmenityAvailability,
  IExtraContentInfo,
  IExtraContentList,
} from 'detail/types';
import { getExtraParams } from 'detail/utils';
import { IScheduleDetail } from 'list/international/types';
import GET_EXTRA_CONTENT from './detailExtraContent.gql';

export const getDefaultExtraContent = () => ({
  extraIcons: {
    MealsAndBeverages: false,
    InFlightEntertainment: false,
    WiFi: false,
    USB: false,
  },
  extraList: [],
});

export const isAmenityAvailable = (availability?: AmenityAvailability) =>
  !!availability &&
  [AmenityAvailability.CHARGEABLE, AmenityAvailability.INCLUDED].includes(
    availability,
  );

const useExtraContents = (schedule: IScheduleDetail[], router: NextRouter) => {
  const defaultExtraContent = getDefaultExtraContent();
  const params = getExtraParams(schedule, router);
  const loadComplete = useRef(false);
  const [res, setExtra] = useState<IExtraContentInfo>(defaultExtraContent);

  const { loading, error, data } = useQuery<IExtraContentList>(
    GET_EXTRA_CONTENT,
    { variables: params },
  );
  if (loading || error || !data) {
    return res;
  }

  if (!loadComplete.current) {
    loadComplete.current = true;
    const { extraContent: result } = data || {
      extraContent: [],
    };
    const extra = result.reduce<IExtraContentInfo>((_result, item) => {
      const {
        mealsAndBeverages,
        inFlightEntertainment,
        wiFi,
        usb,
        insideImages: oInsideImgs,
        outsideImages: oOutsideImgs,
        inFlightMealImages: oInflightImgs,
        equipmentDesc,
        seatArrangement,
        seatInfo,
      } = item;
      const { extraIcons: prevExtraIcons } = _result;

      const [InsideImages, OutsideImages, InFlightMealImages] = [
        oInsideImgs.map(({ imageFileName: ImageFileName }) => ({
          ImageFileName,
          type: 'InsideImages',
        })),
        oOutsideImgs.map(({ imageFileName: ImageFileName }) => ({
          ImageFileName,
          type: 'OutsideImages',
        })),
        oInflightImgs.map(({ imageFileName: ImageFileName }) => ({
          ImageFileName,
          type: 'InFlightMealImages',
        })),
      ];
      const newResult = {
        ..._result,
        extraIcons: {
          MealsAndBeverages:
            !!prevExtraIcons.MealsAndBeverages ||
            isAmenityAvailable(mealsAndBeverages),
          InFlightEntertainment:
            !!prevExtraIcons.InFlightEntertainment ||
            isAmenityAvailable(inFlightEntertainment),
          WiFi: !!prevExtraIcons.WiFi || isAmenityAvailable(wiFi),
          USB: !!prevExtraIcons.USB || isAmenityAvailable(usb),
        },
      };
      newResult.extraList.push({
        ...item,
        InsideImages,
        OutsideImages,
        InFlightMealImages,
        photo: [...InsideImages, ...OutsideImages, ...InFlightMealImages],
        existContent:
          !!equipmentDesc ||
          !!seatArrangement ||
          !!seatInfo ||
          isAmenityAvailable(mealsAndBeverages) ||
          isAmenityAvailable(inFlightEntertainment) ||
          isAmenityAvailable(wiFi) ||
          isAmenityAvailable(usb),
      });
      return newResult;
    }, getDefaultExtraContent());

    setExtra(extra);
  }

  return res;
};

export default useExtraContents;
